import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import axiosInstance from '@/lib/axios';
import { cn } from '@/lib/utils';
import { MentionMenu } from '@/components/mentions/MentionMenu';
import {
  mentionToken,
  MENTION_TOKEN,
  type MentionSuggestion,
} from '@/components/mentions/mention-state';

interface MentionTextareaProps {
  communityId?: string;
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  disabled?: boolean;
}

function serialize(root: HTMLElement) {
  let text = '';
  root.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent ?? '';
      return;
    }
    if (!(node instanceof HTMLElement)) return;
    if (node.dataset.token) {
      text += node.dataset.token;
      return;
    }
    if (node.tagName === 'BR') {
      text += '\n';
      return;
    }
    text += serialize(node);
  });
  return text;
}

function renderStoredValue(root: HTMLElement, value: string) {
  root.replaceChildren();
  let cursor = 0;
  for (const match of value.matchAll(MENTION_TOKEN)) {
    const start = match.index ?? 0;
    if (start > cursor) root.append(document.createTextNode(value.slice(cursor, start)));
    const chip = document.createElement('span');
    chip.contentEditable = 'false';
    chip.dataset.token = match[0];
    chip.className = 'font-bold';
    chip.textContent = `${match[1]}${match[2]}`;
    root.append(chip);
    cursor = start + match[0].length;
  }
  if (cursor < value.length) root.append(document.createTextNode(value.slice(cursor)));
}

function textBeforeCaret(root: HTMLElement) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) return null;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;
  const probe = range.cloneRange();
  probe.selectNodeContents(root);
  probe.setEnd(range.startContainer, range.startOffset);
  return probe.toString();
}

function queryAtCaret(root: HTMLElement) {
  const before = textBeforeCaret(root);
  if (before === null) return null;
  const match = before.match(/(?:^|\s)([@#])([^\s@#]*)$/);
  if (!match) return null;
  return { trigger: match[1] === '#' ? 'hash' : 'at', query: match[2] } as const;
}

export const MentionTextarea = forwardRef<HTMLDivElement, MentionTextareaProps>(
  function MentionTextarea(
    { communityId, value = '', onChange, onBlur, placeholder, maxLength, className, disabled },
    ref
  ) {
    const editorRef = useRef<HTMLDivElement>(null);
    const hostRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [items, setItems] = useState<MentionSuggestion[]>([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [open, setOpen] = useState(false);
    const [menuTop, setMenuTop] = useState<number | null>(null);
    const [menuHeight, setMenuHeight] = useState(0);
    const [trackedValue, setTrackedValue] = useState(value);
    const requestIdRef = useRef(0);

    if (value !== trackedValue) {
      setTrackedValue(value);
      setOpen(false);
      setItems([]);
    }

    useEffect(() => {
      const editor = editorRef.current;
      if (!editor || serialize(editor) === value) return;
      renderStoredValue(editor, value);
    }, [value]);

    function placeMenu() {
      const host = hostRef.current;
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!host || !editor || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.startContainer)) return;
      const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
      if (!rect || rect.height === 0) return;
      setMenuTop(rect.bottom - host.getBoundingClientRect().top + 4);
    }

    function emit() {
      const editor = editorRef.current;
      if (!editor) return;
      const next = serialize(editor).replace(/^\n$/, '');
      if (maxLength && next.length > maxLength) {
        renderStoredValue(editor, value);
        setOpen(false);
        return;
      }
      setTrackedValue(next);
      onChange(next);
    }

    function insertItem(item: MentionSuggestion) {
      const editor = editorRef.current;
      const selection = window.getSelection();
      if (!editor || !selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      const before = textBeforeCaret(editor) ?? '';
      const match = before.match(/(?:^|\s)([@#])([^\s@#]*)$/);
      if (!match || range.startContainer.nodeType !== Node.TEXT_NODE) return;
      const textNode = range.startContainer as Text;
      const removeFrom = range.startOffset - match[1].length - match[2].length;
      textNode.deleteData(removeFrom, match[1].length + match[2].length);
      const chip = document.createElement('span');
      chip.contentEditable = 'false';
      chip.dataset.token = mentionToken(item);
      chip.className = 'font-bold';
      chip.textContent = `${item.trigger === 'at' ? '@' : '#'}${item.label}`;
      const space = document.createTextNode(' ');
      const insertAt = document.createRange();
      insertAt.setStart(textNode, removeFrom);
      insertAt.collapse(true);
      insertAt.insertNode(space);
      insertAt.insertNode(chip);
      const after = document.createRange();
      after.setStart(space, 1);
      after.collapse(true);
      selection.removeAllRanges();
      selection.addRange(after);
      setOpen(false);
      emit();
    }

    useEffect(() => {
      if (!open || !communityId) return;
      const editor = editorRef.current;
      if (!editor) return;
      const found = queryAtCaret(editor);
      if (!found) return;
      const requestId = ++requestIdRef.current;
      const handle = window.setTimeout(async () => {
        try {
          const { data } = await axiosInstance.get(`/api/communities/${communityId}/mentions`, {
            params: { trigger: found.trigger, q: found.query },
          });
          if (requestId !== requestIdRef.current) return;
          const next = Array.isArray(data?.items) ? (data.items as MentionSuggestion[]) : [];
          setItems(next);
          setActiveIndex(0);
        } catch {
          if (requestId === requestIdRef.current) setItems([]);
        }
      }, 200);
      return () => window.clearTimeout(handle);
    }, [open, communityId, value]);

    useLayoutEffect(() => {
      if (!open) return;
      placeMenu();
      const menu = menuRef.current;
      if (!menu) return;
      const observer = new ResizeObserver(() => {
        const next = menu.offsetHeight;
        setMenuHeight((current) => (current === next ? current : next));
      });
      observer.observe(menu);
      return () => observer.disconnect();
    }, [open, value, items]);

    return (
      <div
        ref={hostRef}
        className="relative rounded-xl border border-zinc-300 bg-white shadow-md focus-within:border-ring focus-within:ring-1 focus-within:ring-ring dark:border-zinc-700 dark:bg-black dark:shadow-[0_8px_24px_-12px_rgba(0,0,0,0.5)]"
        style={
          open && menuTop !== null
            ? { minHeight: menuTop + (menuHeight || Math.min(items.length * 48, 240)) + 8 }
            : undefined
        }
      >
        <div
          ref={(node) => {
            editorRef.current = node;
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          role="textbox"
          aria-multiline="true"
          contentEditable={!disabled}
          data-placeholder={placeholder}
          onInput={() => {
            const querying = queryAtCaret(editorRef.current!) !== null;
            setOpen(querying);
            if (querying) placeMenu();
            else setMenuTop(null);
            emit();
          }}
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (!open || items.length === 0) {
              if (event.key === 'Backspace') {
                const selection = window.getSelection();
                const editor = editorRef.current;
                if (!selection || !editor || !selection.isCollapsed) return;
                const range = selection.getRangeAt(0);
                const previous =
                  range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset === 0
                    ? range.startContainer.previousSibling
                    : range.startOffset === 0
                      ? (range.startContainer as HTMLElement).previousSibling
                      : null;
                if (previous instanceof HTMLElement && previous.dataset.token) {
                  event.preventDefault();
                  previous.remove();
                  emit();
                }
              }
              return;
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setActiveIndex((index) => (index + 1) % items.length);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              setActiveIndex((index) => (index - 1 + items.length) % items.length);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              insertItem(items[activeIndex]);
            } else if (event.key === 'Escape') {
              setOpen(false);
            }
          }}
          className={cn(
            'min-h-24 w-full whitespace-pre-wrap bg-transparent px-4 py-3 text-sm text-foreground outline-none empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)] dark:text-neutral-100 dark:empty:before:text-neutral-400',
            className
          )}
        />
        {open && items.length > 0 && menuTop !== null && (
          <div ref={menuRef} className="absolute left-2 right-2 z-20" style={{ top: menuTop }}>
            <MentionMenu items={items} activeIndex={activeIndex} onSelect={insertItem} />
          </div>
        )}
      </div>
    );
  }
);
